import React, { useState, useEffect } from 'react';
import SalesDetailPage from './SalesDetailPage';
import { SORT_TYPES, buildGeneralLedgerColumns, columnsForGroupBy, getDefaultVisibleColumns, getDefaultHiddenColumns, GROUP_BY_CODE, GROUP_BY_CODE_EVENT } from '../constants/generalLedgerColumnsConfig';
import { buildGeneralLedgerFilterGroups } from '../constants/generalLedgerFiltersConfig';
import { compactCurrency } from '../utils/reportingUtils';
import { ToastContainer } from 'react-toastify';
import { toastError } from '../../../shared/toastHelper';
import LedgerLineItemsExportButton from './LedgerLineItemsExportButton';
import {
  GENERAL_LEDGER_REPORT, LEDGER_SCOPE_FILTER_IDS, formatLedgerDay, generalLedgerRowSelection, ledgerLineItemsHref, ledgerScopeParams,
} from '../utils/ledgerLineItems';

const axios = require('axios').default;

const GENERAL_LEDGER_CONFIG = {
  pageTitle: 'General Ledger',
  pageSubtitle: 'Ticket revenue, organizer fees, and taxes grouped by the account code each posts to. Fees and taxes use the code recorded on the ticket when it sold; ticket revenue uses the code on the event today. Refunds post as negative amounts on the day they were issued; a face value refund keeps its fees. Days are counted in each venue\'s time zone. Donations, merchandise, memberships, delivery, ticket insurance and gift cards imported from a venue\'s previous platform post on the day that platform posted them. Gift card rows post to your liability, clearing, fee, promotion, breakage and unclaimed property accounts and are left out of the total; when a card is spent, the revenue comes from the ticket. Map the accounts under Settings → Gift cards. A card bought at one venue and spent at another posts the liability at the first and its release at the second. A balance migrated from Gift Up posts nothing when it is migrated, because it is already on your books, but spending, refunding or voiding it posts as usual. When a card expires, its free value (a complimentary card, or value staff added beyond what was paid) comes off your liability and is credited back to your promotional expense account, dated the day the card expired, and if a refund later reopens the card the expiry is reversed on the day of the refund. Expired paid value posts by the unclaimed property rule you confirmed under Settings → Gift cards: the share your state lets you keep goes to breakage income on the day the card expired, and the share it asks you to report moves to unclaimed property payable once the card has gone unused for the rule\'s dormancy period, and moves back on the day the card is used again. Until you confirm a rule, expired paid value stays in your liability. A card that received value from a merge posts it the way it would have posted on the card it came from. Merges themselves are left out. Click a ticket amount to see the sales and refunds behind it.',
  sidebarKey: 'finances_general_ledger',
  defaultPreset: 'year_to_date',
  dateModeOptions: [{ key: 'created_at', label: 'Collected Date' }],
  urlFilterIds: LEDGER_SCOPE_FILTER_IDS,

  sortTypes: SORT_TYPES,
  localStorageKey: (teamId) => `reportingFinancesGeneralLedgerColumns_v2_${teamId}`,
  firstColumnId: 'general_ledger_code',
  defaultSortRules: [{ field: 'general_ledger_code', direction: 'asc' }],

  kpiFromDetailResponse: true,
  salesDataKey: null,
  venueSeriesKey: null,
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/finances/general-ledger/export`,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/finances/general-ledger/data.json`,
  detailResponseKey: 'rows',
  detailErrorMessage: 'Failed to load general ledger data.',
  detailTableTitle: 'General Ledger',
  emptyMessage: 'No ledger activity in this range.',
  searchPlaceholder: 'Search codes, accounts, and events...',
  paginationLabel: 'rows',
  itemLabel: (count) => `row${count !== 1 ? 's' : ''}`,

  buildKpiCards: (current, prior) => [
    {
      label: 'Total',
      value: compactCurrency(current.amount || 0),
      current: current.amount || 0,
      prior: (prior && prior.amount) || 0,
    },
    {
      label: 'Ticket Revenue',
      value: compactCurrency(current.ticket_revenue || 0),
      current: current.ticket_revenue || 0,
      prior: (prior && prior.ticket_revenue) || 0,
    },
    {
      label: 'Fees',
      value: compactCurrency(current.fees || 0),
      current: current.fees || 0,
      prior: (prior && prior.fees) || 0,
    },
    {
      label: 'Taxes',
      value: compactCurrency(current.taxes || 0),
      current: current.taxes || 0,
      prior: (prior && prior.taxes) || 0,
    },
    ...(current.imported_lines || (prior && prior.imported_lines) ? [{
      label: 'Imported Other Lines',
      value: compactCurrency(current.imported_lines || 0),
      current: current.imported_lines || 0,
      prior: (prior && prior.imported_lines) || 0,
    }] : []),
    ...(current.gift_card_liability != null || (prior && prior.gift_card_liability != null) ? [{
      label: 'Gift Card Liability Change',
      value: compactCurrency(current.gift_card_liability || 0),
      current: current.gift_card_liability || 0,
      prior: (prior && prior.gift_card_liability) || 0,
    }] : []),
  ],
  kpiGridColumns: 4,

  overTimeKey: null,

  topCharts: [],

  groupByOptions: [
    { key: GROUP_BY_CODE, label: 'GL Code' },
    { key: GROUP_BY_CODE_EVENT, label: 'GL Code + Event' },
  ],
  groupByDefault: GROUP_BY_CODE_EVENT,

  renderToolbarExtras: ({ team, csrfToken, dateRange, activeFilters }) => (
    <LedgerLineItemsExportButton
      team={team}
      csrfToken={csrfToken}
      params={{ ...ledgerScopeParams(dateRange, activeFilters), ...ledgerSelectionFilters(activeFilters) }}
    />
  ),

  renderCellValue: (col, row, {
    currencyFormatter: cf, team, dateRange, activeFilters,
  }) => {
    switch (col.id) {
      case 'general_ledger_code': return row.general_ledger_code || '—';
      case 'account': return row.account || '—';
      case 'source': return row.source || '—';
      case 'event_name': return row.event_name || '—';
      case 'event_date': return <span className="text-nowrap">{formatLedgerDay(row.event_date)}</span>;
      case 'amount': return row.drilldown === false ? cf.format(row.amount || 0) : (
        <a
          href={ledgerLineItemsHref(team.id, GENERAL_LEDGER_REPORT, { ...ledgerScopeParams(dateRange, activeFilters), ...generalLedgerRowSelection(row) })}
          title="View line items"
          style={{ color: 'var(--sl-fg-brand, #1982c4)' }}
        >
          {cf.format(row.amount || 0)}
        </a>
      );
      default: return '—';
    }
  },

  filterTitle: 'Filter General Ledger',
  filterSubtitle: 'Apply filters to narrow ledger activity.',
};

const ledgerSelectionFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.source?.length) params.source = activeFilters.source;
  if (activeFilters.general_ledger_code?.length) params.general_ledger_code = activeFilters.general_ledger_code;
  return params;
};

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.source && activeFilters.source.length > 0) {
    params.source = activeFilters.source;
  }
  if (activeFilters.general_ledger_code && activeFilters.general_ledger_code.length > 0) {
    params.general_ledger_code = activeFilters.general_ledger_code;
  }
  LEDGER_SCOPE_FILTER_IDS.forEach((id) => {
    if (activeFilters[id]?.length) params[id] = activeFilters[id];
  });
  return params;
};

function ReportingFinancesGeneralLedgerPage({ team, csrfToken, venueOwnerships, ...props }) {
  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => {
    if (!team?.id) return undefined;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    let ignore = false;
    axios.get(`/teams/${team.id}/reporting/finances/general-ledger/filter_options.json`)
      .then((res) => { if (!ignore) setFilterOptions(res.data || {}); })
      .catch(() => {
        if (!ignore) toastError('Could not load the general ledger filters. Reload the page to try again.');
      });
    return () => { ignore = true; };
  }, [team?.id, csrfToken]);

  const configWithFilters = {
    ...GENERAL_LEDGER_CONFIG,
    columns: buildGeneralLedgerColumns(),
    columnsForGroupBy,
    getDefaultVisibleColumns,
    getDefaultHiddenColumns,
    buildFilterGroups: () => buildGeneralLedgerFilterGroups(filterOptions, venueOwnerships),
    buildDetailApiFilters,
  };
  return (
    <>
      <SalesDetailPage
        {...props}
        team={team}
        csrfToken={csrfToken}
        venueOwnerships={venueOwnerships}
        config={configWithFilters}
      />
      <ToastContainer />
    </>
  );
}

export { GENERAL_LEDGER_CONFIG };
export default ReportingFinancesGeneralLedgerPage;
